import { Popconfirm, Divider } from 'antd5';
import styled from 'styled-components';

import { sortBy, None, truncateTooltipper } from './helpers';
import { formatUnixTimestamp } from 'utils/format';
import { AtlasGqlAttachment } from 'types/atlas-graphql';

const ActionButton = styled.button`
  background: none;
  color: ${({ theme }) => theme.teal};
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
`;

export const name = {
  title: 'File Name',
  key: 'name',
  sorter: sortBy('name'),
  ellipsis: true,
  render: (record: AtlasGqlAttachment) => {
    const { url, name } = record || {};

    return [url, name].every(Boolean) ? (
      <a href={url} rel="noopener noreferrer" target="_blank" data-testid="attachment-name">
        {name}
      </a>
    ) : (
      <None />
    );
  },
};

export const category = {
  title: 'Category',
  key: 'category',
  sorter: sortBy('category'),
  render: (record: AtlasGqlAttachment) => {
    const { category } = record || {};
    const name = category?.name;
    return name ? truncateTooltipper(name, 40) : <None />;
  },
};

export const description = {
  title: 'Description',
  key: 'description',
  sorter: sortBy('description'),
  render: (record: AtlasGqlAttachment) => {
    const { description } = record || {};
    return description ? truncateTooltipper(description, 35) : <None message="no description" />;
  },
};

export const created = {
  title: 'Date Added',
  key: 'created',
  sorter: sortBy('created'),
  render: (record: AtlasGqlAttachment) => {
    const { created } = record || {};
    // created is a millisecond timestamp as a string, so we need to trim 3 characters off the end
    // so that it works with moment
    return created ? formatUnixTimestamp(Number(created.slice(0, -3))) : <None />;
  },
};

export const actions = ({
  onRemove,
  onModalOpen,
  isRemoveVisible = true,
}: {
  onRemove: (record: AtlasGqlAttachment) => void;
  onModalOpen: (record: AtlasGqlAttachment) => void;
  isRemoveVisible: boolean;
}) => ({
  title: 'Actions',
  width: '25%',
  render: (record: AtlasGqlAttachment) => {
    if (!record) return <None message="N/A" />;
    const { canDelete, canUpdate } = record?.permissions;

    return (
      <>
        <a href={`${record.url}`} download={record.name} target="_blank" rel="noopener noreferrer">
          Download
        </a>
        {onRemove && canDelete && isRemoveVisible && (
          <>
            <Divider type="vertical" />
            <Popconfirm title="Sure to remove?" onConfirm={() => onRemove(record)}>
              <ActionButton>Remove</ActionButton>
            </Popconfirm>
          </>
        )}
        {canUpdate && isRemoveVisible && (
          <>
            <Divider type="vertical" />
            <ActionButton data-testid="edit-attachment" onClick={() => onModalOpen(record)}>
              Edit
            </ActionButton>
          </>
        )}
      </>
    );
  },
});
